import {
  Body,
  Button,
  Card,
  Col,
  Divider,
  Flex,
  LazyImage,
  Quantity,
  Row,
  SubTitle,
  ThemeProps,
  styled,
} from '@teko/ds-desktop';
import { DownOutlined, InforFilled } from '@teko/ds-icon';
import { ProductName, ProductPrice } from '@teko/ecommerce';
import { ProductCardPropsHamper, useHamper, useTenantConfig } from 'contexts';
import { MAXIMUM_COUNT_HAMPER } from 'helpers';
import { t } from 'i18n';
import { useState } from 'react';

const StyledCard = styled(Card)`
  padding: 1rem 2rem;
  text-align: center;
  margin-bottom: 16px;
`;

const WarningCard = styled(Card)`
  padding: 12px 8px;
  background-color: #fff6ec;
`;

const Total = styled(ProductPrice)`
  min-height: unset;
  margin-left: 0.3rem;
  color: rgb(235, 33, 1);
  span {
    font-size: 14px;
  }
`;
const StyledButton = styled(Button)`
  margin-top: 0.875rem;
`;

export const HamperSummary = () => {
  const {
    increaseCurrentHamperQuantity,
    decreaseCurrentHamperQuantity,
    setCurrentHamperQuantity,
    priceSummary,
    priceAvailableHamper,
    allPriceSummary,
    handleBuyNowHamper,
    availableHamper,
    updateCurrentConfig,
    addHamper,
    removeHamper,
    minusHamper,
    onChangeQuantityHamper,
  } = useHamper();

  const { zaloUrl } = useTenantConfig();

  const countHamper = priceSummary.reduce((prev, cur) => {
    return prev + cur.quantity;
  }, 0);

  const countAvailableHamper = availableHamper?.reduce((prev, cur) => {
    return prev + cur.quantity;
  }, 0);

  return (
    <Col>
      <StyledCard>
        <div>{t('Tổng giá trị đơn hàng')}</div>
        {!!allPriceSummary && (
          <Row justify="space-between" align="middle" marginTop="s16">
            <StyledContent>
              {t('Giỏ quà tự chọn')} ({countHamper})
            </StyledContent>
            <Total price={allPriceSummary} />
          </Row>
        )}
        {!!priceAvailableHamper && (
          <Row justify="space-between" align="middle" marginTop="s16">
            <StyledContent>
              {t('Giỏ quà gói sẵn')} ({countAvailableHamper})
            </StyledContent>
            <Total price={priceAvailableHamper} />
          </Row>
        )}
        <Divider color="#f0f0f0" />
        <Row justify="space-between" align="middle">
          <StyledContent>{t('Tổng tạm tính')}</StyledContent>
          <Total price={allPriceSummary + (priceAvailableHamper || 0)} />
        </Row>
        <StyledButton variant="primary" block onClick={handleBuyNowHamper}>
          {t('Mua ngay')}
        </StyledButton>
        <a target="_blank" href={zaloUrl} rel="noopener noreferrer">
          <StyledButton variant="default" block>
            {t('Nhận tư vấn')}
          </StyledButton>
        </a>
        <Divider color="#f0f0f0" />
        <Row justify="space-between" align="middle">
          <StyledTitle> {t('Giỏ quà tự chọn')}</StyledTitle>
          <StyledContent>
            ({priceSummary.length}/{MAXIMUM_COUNT_HAMPER})
          </StyledContent>
        </Row>
        {priceSummary.map((p, index) => {
          return (
            <StyledHamper direction="column" width="100%" key={index}>
              <Row
                width="100%"
                justify="space-between"
                align="middle"
                marginTop="s16"
              >
                {t('Giỏ quà tự chọn')} {p.index + 1}
                <Quantity
                  quantity={p.quantity}
                  setQuantity={(newQuantity) =>
                    setCurrentHamperQuantity(newQuantity, p.index)
                  }
                  onMinus={
                    p.quantity === 1
                      ? () => updateCurrentConfig({}, p.index)
                      : () => decreaseCurrentHamperQuantity(p.index)
                  }
                  onPlus={() => increaseCurrentHamperQuantity(p.index)}
                  min={0}
                  max={999}
                  color="primary500"
                />
              </Row>
              <ProductsCollapse products={p.products} />
              <Row justify="space-between" width="100%" align="middle">
                {t('Chi phí dự kiến')}
                <Total price={p.price} />
              </Row>
            </StyledHamper>
          );
        })}
        <Divider color="#f0f0f0" />
        <Row justify="space-between" align="middle">
          <StyledTitle>{t('Giỏ quà gói sẵn')}</StyledTitle>
          <StyledContent>({availableHamper.length})</StyledContent>
        </Row>
        {availableHamper.map((ah) => {
          return (
            <Flex key={ah.sku} direction="column">
              <Flex
                justify="start"
                marginTop="s8"
                width="100%"
                direction="row"
                align="center"
              >
                <LazyImage
                  width={64}
                  height={64}
                  src={ah?.imageUrl}
                  border
                  borderRadius={8}
                  contain
                />
                <Flex
                  direction="column"
                  justify="start"
                  width="50%"
                  paddingLeft="s4"
                  paddingRight="s4"
                >
                  <StyledProductName
                    numberOfLine={2}
                    name={ah.name || t('Tên sản phẩm đang được cập nhật')}
                  />
                </Flex>
                <Flex direction="row" justify="end">
                  <Quantity
                    quantity={ah.quantity}
                    setQuantity={(q) => onChangeQuantityHamper(ah.sku, q)}
                    onMinus={() =>
                      ah.quantity === 1
                        ? removeHamper(ah.sku)
                        : minusHamper(ah.sku)
                    }
                    onPlus={() => addHamper(ah.sku)}
                    min={0}
                    max={999}
                    color="primary500"
                  />
                </Flex>
              </Flex>
              <Flex
                direction="column"
                width="100%"
                justify="end"
                align="end"
                marginTop="s12"
              >
                <StyledPrice price={ah.price * (ah.quantity || 1)} />
              </Flex>
            </Flex>
          );
        })}
      </StyledCard>
      <WarningCard>
        <Flex direction="row" align="center" justify="start">
          <InforFilled size={24} color="#FAAD14" />
          <Flex width="90%" marginLeft="s12" direction="column">
            <Body level={1} weight="regular" color="warning">
              {t(
                'Đối với giỏ quà tự chọn, mức giá hiển thị chưa bao gồm khay và nơ. Siêu thị sẽ liên hệ Quý khách để tư vấn và xác nhận chi phí trước khi đóng gói',
              )}
            </Body>
          </Flex>
        </Flex>
      </WarningCard>
    </Col>
  );
};

const ProductsCollapse = ({
  products = [],
}: {
  products: ProductCardPropsHamper[];
}) => {
  const [show, setShow] = useState(false);

  return (
    <Collapse>
      <CollapseHeader onClick={() => setShow(!show)}>
        <SubTitle color="#82869e" level={4} weight="medium">
          {t('Xem chi tiết')} ({products.length} {t('sản phẩm')})
        </SubTitle>
        <StyledDownOutlined
          className={show ? 'active' : ''}
          color="textPrimary"
        />
      </CollapseHeader>
      <CollapseContent className={show ? 'active' : ''}>
        <StyledFlex direction="column">
          {products.map((product, index) => {
            return (
              <StyledProductItem
                key={index}
                justify="start"
                marginTop="s8"
                width="100%"
                direction="row"
                align="center"
              >
                <LazyImage
                  width={40}
                  height={40}
                  src={product?.imageUrl}
                  border
                  borderRadius={8}
                  contain
                />
                <Flex
                  direction="column"
                  justify="start"
                  width="70%"
                  paddingLeft="s4"
                >
                  <StyledProductName
                    name={product.name || t('Tên sản phẩm đang được cập nhật')}
                  />
                  <SKU>SL: {product.quantity}</SKU>
                </Flex>
                {product.quantity && (
                  <Row
                    width="30%"
                    paddingLeft="s4"
                    justify="end"
                    align="middle"
                  >
                    {product && product.price && (
                      <StyledPrice
                        price={
                          product.price.latestPrice * (product.quantity || 1)
                        }
                      />
                    )}
                  </Row>
                )}
              </StyledProductItem>
            );
          })}
        </StyledFlex>
      </CollapseContent>
    </Collapse>
  );
};

const StyledDownOutlined = styled(DownOutlined)`
  transition: 0.2s;
  &.active {
    transform: rotate(180deg);
  }
`;

const Collapse = styled.div`
  width: 100%;
  margin-top: 16px;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 16px;
  margin-bottom: 12px;
`;

const CollapseHeader = styled.div`
  display: flex;
  justify-content: space-between;
  height: 1.5rem;
  align-items: center;
  cursor: pointer;
  user-select: none;
  width: 100%;
`;

const CollapseContent = styled.div(
  ({ theme }: ThemeProps) => `
  height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  gap: 0.25rem;
  &.active {
    margin-top: ${theme?.spacing.s12};
    height: auto;
  }
`,
);

const StyledFlex = styled(Flex)`
  flex-wrap: wrap;
`;

const StyledHamper = styled(Flex)`
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 8px;
  margin-top: 16px;
`;

const StyledProductItem = styled(Flex)`
  cursor: pointer;
  padding: 4px;
  border-radius: 8px;
  :hover {
    background-color: #f3f4f6;
  }
`;

const StyledProductName = styled(ProductName)<{ numberOfLine?: number }>(
  ({ numberOfLine = 1 }) => `
  overflow: hidden;
  text-align: left;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${numberOfLine};
`,
);

const SKU = styled.span`
  color: #82869e;
  font-weight: 400;
  font-size: 12px;
  text-align: left;
`;

const StyledPrice = styled(ProductPrice)`
  span {
    font-size: 14px;
  }
`;

const StyledTitle = styled(Body)`
  font-weight: 500;
  font-size: 14px;
`;

const StyledContent = styled(Body)`
  font-size: 14px;
`;
